import { normalizeAudioSourceUrl } from './audio-url-normalization'

type PlaybackSoundLike = {
  file_url?: string | null
  preview_audio_path?: string | null
  audioUrl?: string | null
}

export interface PlaybackAudioSource {
  /** Key for useAudioUrl / storageApi.getAudioUrl. */
  fileKey: string | null
  /** A URL that needs no further resolution, when one is already known. */
  resolvedUrl: string | null
}

/**
 * Whether this browser can decode the AAC-LC previews. Every mainstream
 * Safari, Chrome, Edge and Firefox can; open-source Chromium builds and
 * Firefox on Linux without system codecs cannot, and the player has no
 * format fallback of its own, so those keep playing the WAV. Answered once
 * per page; without a document (server render) the preview is assumed.
 */
let aacSupport: boolean | undefined
function canPlayAacPreview(): boolean {
  if (typeof document === 'undefined') return true
  if (aacSupport === undefined) {
    try {
      // audio-egress-allow: canPlayType probe; the element never gets a src
      aacSupport = document.createElement('audio').canPlayType('audio/mp4; codecs="mp4a.40.2"') !== ''
    } catch {
      aacSupport = false
    }
  }
  return aacSupport
}

/** Exported for tests only: forget the cached codec answer. */
export function _resetAacSupportForTesting(): void {
  aacSupport = undefined
}

/**
 * Public URL of an object in the audio-files bucket, built the same way
 * storageApi.getAudioUrl builds one for a relative path, so the preloader and
 * the player always agree on the exact URL.
 */
function audioFilesPublicUrl(path: string): string | null {
  const base = normalizeAudioSourceUrl(process.env.NEXT_PUBLIC_SUPABASE_URL ?? '').replace(/\/+$/, '')
  return base ? `${base}/storage/v1/object/public/audio-files/${path}` : null
}

/**
 * Where playback, and only playback, reads a sound's audio from.
 *
 * The compressed AAC preview (`preview_audio_path`) when the row has one and
 * the browser can decode AAC: about 1/15th of the WAV's bytes, which Supabase
 * bills as egress on every play. Otherwise the WAV, through the
 * server-resolved `audioUrl` when present.
 *
 * Never use this for a download, a trim or Chime Studio: those must get the
 * PCM WAV at `file_url`. The server-resolved `audioUrl` always points at the
 * WAV, so it is only passed through when no preview is used; passing it with
 * the preview key would seed useAudioUrl's cache with the wrong file.
 */
export function playbackAudioSource(sound: PlaybackSoundLike | null | undefined): PlaybackAudioSource {
  if (!sound) return { fileKey: null, resolvedUrl: null }

  const preview = sound.preview_audio_path
  if (preview && canPlayAacPreview()) return { fileKey: preview, resolvedUrl: audioFilesPublicUrl(preview) }

  return { fileKey: sound.file_url || null, resolvedUrl: sound.audioUrl ?? null }
}
